import React from 'react'

import { Label } from '../label'
import * as S from './switch.styled'
import { SwitchProps } from './switch.types'

export const Switch = ({
  checked = false,
  size = 'medium',
  label,
  inlineLabel,
  tooltip,
  helperText,
  error,
  required,
  disabled = false,
  readOnly = false,
  className,
  id,
  name,
  withReservedFooter,
  onChange,
  ...props
}: SwitchProps) => {
  const hasError = !!error
  const hasLabel = !!label
  // Note: a checkbox input ignores the native `readOnly` attribute. A read-only switch stays a
  // controlled input whose change handler does nothing, so React puts `checked` straight back
  // after any click or Space (cancelling the click instead double-reverts in some environments).
  const isInteractive = !disabled && !readOnly

  const message = error ?? helperText
  const shouldShowFooter = withReservedFooter || !!message
  const inputId = id ?? name
  const helperId = message ? `${inputId}-message` : undefined

  const footer = shouldShowFooter && (
    <S.Footer $size={size}>
      {message && (
        <S.Message id={helperId} $hasError={hasError} role={error ? 'alert' : undefined}>
          {message}
        </S.Message>
      )}
    </S.Footer>
  )

  return (
    <S.Container
      className={className}
      $size={size}
      $hasLabel={hasLabel}
      $shouldShowFooter={shouldShowFooter}
      data-component-id="Switch"
    >
      {hasLabel && (
        <S.LabelColumn>
          <Label
            htmlFor={inputId}
            size={size}
            disabled={disabled}
            hasError={hasError}
            tooltip={tooltip}
            required={required}
          >
            {label}
          </Label>
          {footer}
        </S.LabelColumn>
      )}
      <S.SwitchControl>
        <S.SwitchLabel $disabled={disabled} $readOnly={readOnly} $checked={checked}>
          <S.SwitchInput
            type="checkbox"
            id={inputId}
            name={name}
            checked={checked}
            disabled={disabled}
            required={required}
            aria-readonly={readOnly || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={helperId}
            readOnly={readOnly}
            onChange={readOnly ? () => {} : onChange}
            {...props}
          />
          <S.SwitchTrack
            $size={size}
            $checked={checked}
            $disabled={disabled}
            $interactive={isInteractive}
          >
            <S.SwitchThumb $size={size} $checked={checked} $disabled={disabled} />
          </S.SwitchTrack>
        </S.SwitchLabel>
        {inlineLabel && <S.InlineLabel size={size}>{inlineLabel}</S.InlineLabel>}
      </S.SwitchControl>
      {!hasLabel && footer}
    </S.Container>
  )
}
